import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { LayoutDashboard, Package, RotateCcw, Heart, MapPin, User, Menu, X, LogOut, Store, ChevronLeft, Wallet, CreditCard, Bell } from "lucide-react";
import { ChatBubbleLeftRightIcon, StarIcon, TicketIcon } from "@heroicons/react/24/outline";
import { useAuth } from "@/context/AuthContext";
import { useChat } from "@/context/ChatContext";
import { useI18n } from "@/context/I18nContext";
import { useCurrency } from "@/context/CurrencyContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Breadcrumbs } from "@/components/Breadcrumbs";

export const BUYER_NAV = [
  { key: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { key: "orders", label: "My Orders", icon: Package },
  { key: "messages", label: "Messages", icon: ChatBubbleLeftRightIcon, badge: "messages" },
  { key: "returns", label: "Returns & Refunds", icon: RotateCcw },
  { key: "reviews", label: "Reviews", icon: StarIcon },
  { key: "coupons", label: "Coupons", icon: TicketIcon },
  { key: "wallet", label: "Wallet & Points", icon: Wallet },
  { key: "payments", label: "Payment Methods", icon: CreditCard },
  { key: "wishlist", label: "Wishlist", icon: Heart },
  { key: "addresses", label: "Addresses", icon: MapPin },
  { key: "profile", label: "Profile", icon: User },
];

const NavList = ({ active, go }) => {
  const chat = useChat();
  const unread = chat?.unread || 0;
  return (
    <nav className="flex flex-col gap-1">
      {BUYER_NAV.map(({ key, label, icon: Icon, badge }) => (
        <button key={key} data-testid={`buyer-nav-${key}`} onClick={() => go(key)}
          className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors text-left
            ${active === key ? "bg-orange-500 text-white shadow-[0_4px_14px_rgba(234,88,12,0.35)]" : "text-muted-foreground hover:bg-orange-50 hover:text-orange-600"}`}>
          <Icon className="w-5 h-5 shrink-0" />
          <span className="flex-1">{label}</span>
          {badge === "messages" && unread > 0 && (
            <span data-testid="buyer-nav-messages-badge" className={`min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-bold flex items-center justify-center ${active === key ? "bg-white text-orange-600" : "bg-orange-500 text-white"}`}>{unread}</span>
          )}
        </button>
      ))}
    </nav>
  );
};

export const BuyerShell = ({ active, onNavigate, title, subtitle, children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { lang, setLang, langs } = useI18n();
  const { currency, setCurrency, currencies } = useCurrency();
  const [mobileNav, setMobileNav] = useState(false);
  const [notifs, setNotifs] = useState([]);
  useEffect(() => { api.get("/notifications").then((r) => setNotifs(r.data || [])).catch(() => {}); }, []);
  const unread = notifs.filter((n) => !n.read).length;
  const markRead = () => setNotifs((ns) => ns.map((n) => ({ ...n, read: true })));
  const go = (key) => { setMobileNav(false); onNavigate(key); };
  const initials = (user?.name || "U").trim().charAt(0).toUpperCase();
  const activeLabel = BUYER_NAV.find((n) => n.key === active)?.label;

  const Profile = () => (
    <div className="flex items-center gap-3 px-3 py-4 mb-2 rounded-2xl bg-gradient-to-br from-orange-500 to-amber-500 text-white">
      <span className="w-11 h-11 rounded-full bg-white/25 flex items-center justify-center font-black text-lg">{initials}</span>
      <div className="min-w-0">
        <p className="font-bold truncate">{user?.name}</p>
        <p className="text-xs opacity-90 truncate">{user?.email}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-muted/30">
      {/* Top bar */}
      <div className="sticky top-0 z-40 bg-white dark:bg-card border-b border-border/60">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 h-14 flex items-center gap-3">
          <Sheet open={mobileNav} onOpenChange={setMobileNav}>
            <SheetTrigger asChild>
              <Button data-testid="buyer-mobile-menu" variant="ghost" size="icon" className="lg:hidden" aria-label="Open menu"><Menu className="w-5 h-5" /></Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 p-4">
              <SheetTitle className="sr-only">Account menu</SheetTitle>
              <SheetDescription className="sr-only">Navigate your buyer account</SheetDescription>
              <Profile />
              <NavList active={active} go={go} />
            </SheetContent>
          </Sheet>
          <Link to="/" className="font-display font-black text-lg">Xurray<span className="text-primary">Mart</span></Link>
          <span className="text-muted-foreground text-sm hidden sm:inline">· My Account</span>
          <div className="ml-auto flex items-center gap-1">
            {/* Currency */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-testid="buyer-currency" variant="ghost" size="icon" className="rounded-full font-bold text-sm">
                  {currencies.find((c) => c.code === currency)?.symbol || "€"}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {currencies.map((c) => (
                  <DropdownMenuItem key={c.code} data-testid={`buyer-currency-${c.code}`} onClick={() => setCurrency(c.code)} className={currency === c.code ? "bg-accent/50 font-semibold" : ""}>
                    <span className="mr-2 w-4 text-center font-bold">{c.symbol}</span>{c.code}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Language */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-testid="buyer-language" variant="ghost" size="icon" className="rounded-full">
                  <span className="text-lg leading-none">{langs.find((l) => l.code === lang)?.flag || "🌐"}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {langs.map((l) => (
                  <DropdownMenuItem key={l.code} data-testid={`buyer-lang-${l.code}`} onClick={() => setLang(l.code)} className={lang === l.code ? "bg-accent/50 font-semibold" : ""}>
                    <span className="mr-2 text-lg leading-none">{l.flag}</span>{l.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Notifications */}
            <DropdownMenu onOpenChange={(o) => { if (o) markRead(); }}>
              <DropdownMenuTrigger asChild>
                <Button data-testid="buyer-notifications" variant="ghost" size="icon" className="rounded-full relative">
                  <Bell className="w-5 h-5" />
                  {unread > 0 && <span data-testid="buyer-notif-count" className="absolute -top-0.5 -right-0.5 min-w-4 h-4 px-1 rounded-full bg-destructive text-white text-[10px] font-bold flex items-center justify-center">{unread > 9 ? "9+" : unread}</span>}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80">
                <DropdownMenuLabel>Notifications</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {notifs.length === 0 && <div className="px-3 py-6 text-center text-sm text-muted-foreground">You're all caught up</div>}
                {notifs.slice(0, 8).map((n) => (
                  <div key={n.notif_id} data-testid={`buyer-notif-${n.notif_id}`} className={`px-3 py-2 text-sm border-b border-border/40 ${!n.read ? "bg-primary/5" : ""}`}>
                    <p className="leading-snug">{n.message || n.title || n.body}</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">{new Date(n.created_at).toLocaleString()}</p>
                  </div>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <Button data-testid="back-to-shop" variant="outline" size="sm" className="rounded-full" onClick={() => navigate("/products")}>
              <ChevronLeft className="w-4 h-4 mr-1" />Continue shopping
            </Button>
            <Button data-testid="buyer-logout" variant="ghost" size="icon" aria-label="Log out" onClick={() => { logout(); navigate("/"); }}><LogOut className="w-5 h-5" /></Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-3 sm:px-6 py-6 flex gap-6">
        {/* Sidebar */}
        <aside className="hidden lg:block w-64 shrink-0">
          <div className="sticky top-20 bg-white dark:bg-card rounded-2xl p-3 shadow-[0_4px_20px_rgba(0,0,0,0.05)]">
            <Profile />
            <NavList active={active} go={go} />
            <div className="border-t my-3" />
            <button onClick={() => navigate("/sell")} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-orange-600 hover:bg-orange-50 transition-colors">
              <Store className="w-5 h-5" />Become a seller
            </button>
          </div>
        </aside>

        {/* Main */}
        <main className="flex-1 min-w-0">
          {activeLabel && <div className="mb-4"><Breadcrumbs items={[{ label: "My Account" }, { label: activeLabel }]} onHome={() => go("dashboard")} /></div>}
          {title && <div className="mb-5"><h1 className="font-display font-bold text-2xl sm:text-3xl">{title}</h1>{subtitle && <p className="text-muted-foreground text-sm mt-0.5">{subtitle}</p>}</div>}
          <AnimatePresence mode="wait">
            <motion.div key={active} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.22, ease: "easeOut" }}>
              {children}
            </motion.div>
          </AnimatePresence>
        </main>
      </div>
    </div>
  );
};
